import React, { createContext, useContext } from 'react';
import styles from './styles.module.scss';

type ChipGroupContextValue = {
  isMulti: boolean;
  selectedValues: string[];
  onSelect: (value: string) => void;
};

export const ChipGroupContext = createContext<ChipGroupContextValue>({
  isMulti: false,
  selectedValues: [],
  onSelect: () => {},
});

export const useChipGroup = (): ChipGroupContextValue => useContext(ChipGroupContext);

type ChipGroupBaseProps = {
  /** アクセシビリティ用ラベル */
  'aria-label'?: string;
  /** アクセシビリティ用ラベル参照 ID */
  'aria-labelledby'?: string;
  children: React.ReactNode;
  /** 追加のクラス名 */
  className?: string;
};

type ChipGroupSingleProps<T extends string = string> = ChipGroupBaseProps & {
  /**
   * 単一選択モード（デフォルト）。
   * value は選択中の値（string）。
   */
  isMulti?: false;
  /** 選択中の値 */
  value?: T;
  /** 値が変更されたときのコールバック */
  onChange?: (value: T) => void;
};

type ChipGroupMultiProps<T extends string = string> = ChipGroupBaseProps & {
  /**
   * 複数選択モード。
   * value は選択中の値の配列。
   */
  isMulti: true;
  /** 選択中の値の配列 */
  value?: T[];
  /** 値が変更されたときのコールバック */
  onChange?: (values: T[]) => void;
};

type ChipGroupProps<T extends string = string> = ChipGroupSingleProps<T> | ChipGroupMultiProps<T>;

export const ChipGroup = <T extends string = string>({
  children,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  ...rest
}: ChipGroupProps<T>) => {
  const selectedValues: string[] = rest.isMulti
    ? (rest.value ?? [])
    : rest.value != null
      ? [rest.value]
      : [];

  const handleSelect = (clickedValue: string) => {
    if (!rest.onChange) return;

    if (rest.isMulti) {
      const current = rest.value ?? [];
      const next = current.includes(clickedValue as T)
        ? current.filter(v => v !== clickedValue)
        : [...current, clickedValue as T];
      rest.onChange(next);
    } else {
      rest.onChange(clickedValue as T);
    }
  };

  return (
    <ChipGroupContext.Provider
      value={{ isMulti: rest.isMulti ?? false, selectedValues, onSelect: handleSelect }}
    >
      <fieldset
        className={[styles.chipGroup, className].filter(Boolean).join(' ')}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
      >
        {children}
      </fieldset>
    </ChipGroupContext.Provider>
  );
};
